import React, { useState, useEffect } from 'react';
import { Play, Star, X, Clock, Award } from 'lucide-react';

/**
 * Video Testimonials — exact replica of www.agseosydney.com.au style.
 * 3 real testimonials with real Vimeo videos and snapshots from our sister site.
 */
const defaultItems = [
  {
    title: 'GWM IS MAGIC',
    quote:
      'Ben Kierath chose Green Web Marketing for two of his businesses, Hoselink and True Protein. Extraordinary results across the board.',
    name: 'Ben Kierath',
    role: 'CEO — Hoselink & True Protein',
    duration: '01:43',
    tenure: 'Been Our Client for 9+ Years',
    image: '/assets/images/testimonials/ben-kierath.jpg',
    vimeoId: '227690478',
  },
  {
    title: 'GWM IS PROFICIENT',
    quote:
      'The talented people at GWM Ranked our website on Page 1 of Google for our main keywords such as Corporate Training in only two months.',
    name: 'Scott Epp',
    role: 'Executive Manager — Breakthrough Corporate Training',
    duration: '03:38',
    tenure: 'Long-term client',
    image: '/assets/images/testimonials/scott-epp.jpg',
    vimeoId: '237336727',
  },
  {
    title: 'GWM IS HONEST',
    quote:
      'Within a short period of time, we have 18 keywords on page #1 of Google which is an outstanding result and good return on investment.',
    name: 'Ziad',
    role: 'Total Appliance Repair Centre',
    duration: '01:36',
    tenure: 'Been Our Client for 9+ Years',
    image: '/assets/images/testimonials/ziad.jpg',
    vimeoId: '237331597',
  },
];

const VideoTestimonials = ({
  heading = 'What Our Clients',
  headingItalic = 'Say',
  subheading = 'Some of our clients have been with us for over 10 years.',
  items = defaultItems,
  background = 'light',
}) => {
  const [activeVideo, setActiveVideo] = useState(null);
  const isDark = background === 'dark';

  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeVideo]);

  // Close modal on Escape
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setActiveVideo(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <section
      className={`relative py-24 md:py-32 ${isDark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`}
      data-testid="video-testimonials"
      id="testimonials"
    >
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <p
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] mb-5 ${
              isDark ? 'bg-white/10 text-[#86efac]' : 'bg-[#15803d]/10 text-[#166534]'
            }`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#15803d]" />
            Testimonials
          </p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            {heading}{' '}
            <span className="italic font-serif font-medium text-[#15803d]">{headingItalic}</span>
          </h2>
          <p className={`mt-5 text-lg md:text-xl leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {subheading}
          </p>
          <div className="mt-6 mx-auto h-1 w-20 rounded-full bg-gradient-to-r from-[#15803d] to-[#22c55e]" />
        </div>

        {/* Stacked landscape cards — replica of www.agseosydney.com.au */}
        <div className="space-y-8">
          {items.map((t, idx) => (
            <article
              key={idx}
              data-testid={`video-testimonial-${idx}`}
              className={`group relative rounded-3xl overflow-hidden border-2 shadow-lg hover:shadow-2xl transition-all ${
                isDark
                  ? 'bg-slate-800/60 border-[#15803d]/40 hover:border-[#15803d]'
                  : 'bg-white border-[#15803d]/40 hover:border-[#15803d]'
              }`}
            >
              {/* Big landscape thumbnail with play button */}
              <button
                type="button"
                onClick={() => setActiveVideo(t)}
                aria-label={`Play testimonial from ${t.name}`}
                className="group/play relative block w-full aspect-[16/9] overflow-hidden cursor-pointer"
              >
                <img
                  src={t.image}
                  alt={`${t.name} video testimonial — GWM SEO client`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover/play:scale-105"
                />
                {/* Dark gradient for readability of badges */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30" />

                {/* Top-left tenure badge */}
                {t.tenure && (
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-[#f97316] text-white px-4 py-2 text-sm font-bold shadow-lg">
                    <Award size={14} />
                    {t.tenure}
                  </div>
                )}

                {/* Top-right duration badge */}
                <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-black/80 backdrop-blur text-white px-3.5 py-1.5 text-sm font-semibold shadow-lg">
                  <Clock size={14} /> {t.duration}
                </div>

                {/* Center play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="relative flex h-20 w-20 md:h-28 md:w-28 items-center justify-center rounded-full bg-[#15803d] shadow-2xl transition-all group-hover/play:scale-110">
                    <span className="absolute inset-0 rounded-full bg-[#15803d] animate-ping opacity-40" />
                    <Play size={36} className="ml-1.5 fill-white text-white relative" />
                  </span>
                </div>
              </button>

              {/* Text body */}
              <div className="p-7 md:p-9">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <div>
                    <h3 className={`text-2xl md:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {t.title}
                    </h3>
                    <p className={`text-sm md:text-base ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <span className="font-semibold text-[#15803d]">{t.name}</span> — {t.role}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={18} className="fill-[#fbbf24] text-[#fbbf24]" />
                    ))}
                  </div>
                </div>
                <p className={`text-base md:text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  &ldquo;{t.quote}&rdquo;
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Video modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fadeIn"
          onClick={() => setActiveVideo(null)}
          data-testid="video-modal"
        >
          <button
            type="button"
            onClick={() => setActiveVideo(null)}
            aria-label="Close video"
            className="absolute top-6 right-6 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all hover:scale-110"
          >
            <X size={24} />
          </button>
          <div
            className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title={`${activeVideo.name} testimonial`}
              src={`https://player.vimeo.com/video/${activeVideo.vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoTestimonials;
